/**
 * URL state schema + pure parse/serialize for the `/discover` surface.
 *
 * URL is the single source of truth for `/discover`. Every filter, view mode,
 * sort, and free-text query lives in `searchParams`. This module defines the
 * canonical shape via Zod and provides pure transforms used by both the SSR
 * page and the client hook (`useDiscoverParams`).
 *
 * Conventions (WEB-45):
 * - Multi-select params (`category`, `neighborhood`, `venue`) are comma-joined;
 *   serialization dedupes and sorts entries to keep cache keys stable.
 * - Top-level keys are emitted in alphabetical order so cache keys are
 *   deterministic across calls.
 * - `view` defaults to `'list'` and is omitted from the URL when default
 *   (cleaner shareable links).
 * - Unknown values for enum fields fall back to defaults silently.
 * - `date` accepts a named preset or an ISO range `YYYY-MM-DD..YYYY-MM-DD`;
 *   anything else is rejected (i.e. dropped to `undefined`).
 *
 * WEB-1228 (M3 wave 0, rulings R7 / R8): the `time`, `vibe` and `genre` keys
 * are gone. Their controls (time-of-day blocks, Vibes, Genre) had no real data
 * behind them, and a stale `?time=` / `?vibe=` / `?genre=` link must not
 * narrow results with no visible control, so those params are now ignored.
 */
import { z } from 'zod';

import { CITY_RECORDS } from '@loci/city-editions';
import { DATE_WINDOW_PRESETS, dateWindowPreset } from '@loci/validation';

import { normalizePricePresetParam, type PricePreset } from '@/lib/discover/pricePreset';

// API-1820 — the presets are `DATE_WINDOW_PRESETS` from `@loci/validation`,
// the one window module web and the API share. `this-week` (now through
// Sunday, city-local) is what the nav and footer "This week" links send.
// `next-week` is not a preset: a stray `?date=next-week` is dropped.
const DATE_PRESETS = DATE_WINDOW_PRESETS;
const ISO_DATE_RANGE = /^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/;

// WEB-665 / DES-64 (O-2 ruling): Discovery v2 drops the tab set to List | Map.
// `parseEnum`'s unknown-value fallback is the whole mechanism — a stray
// `?view=calendar` (or any other unrecognized value) simply falls through to
// `DEFAULT_VIEW` ('list') below. No consumer-level redirect or normalisation
// needed; see `docs/design-system/discovery-v2-rulings.md` O-2.
const VIEWS = ['list', 'map'] as const;
// FRE-899: `price` (low-to-high) is a real backend order (`compareEventsForSort`
// `case 'price'`, `discoverSortSchema`). WEB-666 dropped `popularity`
// ("Trending") from the menu; a stray `?sort=popularity` or `?sort=trending`
// still falls through `parseEnum` to `DEFAULT_SORT`. The server keeps
// `popularity`. WEB-1221: the UI default is date order. `recommended` is not
// a menu key — `parseSort` maps a bookmarked `?sort=recommended` onto `date`
// without the unknown-value debug log, and the next serialize drops it.
// WEB-1228 (R2): `distance` is gone. It measured from the city centroid, not
// from the reader, so a "from me" distance sort was a claim the query could
// not back. A bookmarked `?sort=distance` parses as `date`, like `recommended`.
const SORTS = ['date', 'price'] as const;

export const DEFAULT_VIEW = 'list' satisfies (typeof VIEWS)[number];
export const DEFAULT_SORT = 'date' satisfies (typeof SORTS)[number];
/**
 * City anchor default radius in km. Read off Charlotte's record in
 * `CITY_RECORDS` (COR-155) — the same value `getCityAnchor` and the backend
 * `resolveCityAnchor` read — and equal to the backend `discoverQuerySchema` default. The serializer omits
 * `radius_km` from the URL when the value equals this default so shareable
 * URLs stay clean and SSR cache keys remain deterministic.
 */
export const DEFAULT_RADIUS_KM: number = CITY_RECORDS.CLT.geography.defaultAnchor.radiusKm;

export type DateValue = (typeof DATE_PRESETS)[number] | string;
export type ViewMode = (typeof VIEWS)[number];
export type SortMode = (typeof SORTS)[number];

const DateValueSchema = z
  .string()
  .refine(
    (value) => (DATE_PRESETS as readonly string[]).includes(value) || ISO_DATE_RANGE.test(value),
    { message: 'Invalid date value' },
  );

const ViewSchema = z.enum(VIEWS);
const SortSchema = z.enum(SORTS);

/**
 * Canonical shape after parsing. All fields are present so callers don't have
 * to defensively check for undefined collections.
 */
export interface DiscoverParams {
  date?: DateValue;
  category: string[];
  neighborhood: string[];
  /**
   * Active price preset (WEB-656 / DPL-06) — `'free' | '0-25' | '25-plus'`,
   * mutually exclusive, or `undefined` for "no price filter". Parsing always
   * normalizes to one of these three canonical values (or `undefined`); see
   * {@link normalizePricePresetParam} for the retired slider's `min-max` /
   * `min+` migration grammar. Never a free-form range string — that shape
   * belonged to the retired `PriceRangeSlider` and is only accepted here as
   * legacy input to migrate away from.
   */
  price?: PricePreset;
  /**
   * "+N not listed" (WEB-1230, ruling R6): with a price preset active, also
   * show events that have no listed price. Meaningless without a preset, so
   * the serializer writes `priceUnlisted=true` only alongside `price`, and
   * "off" is absence, like {@link DiscoverParams.followedOnly}.
   */
  priceUnlisted?: boolean;
  /**
   * Search radius in km. Omitted from the URL when equal to
   * {@link DEFAULT_RADIUS_KM} (the CLT city-anchor default the backend already
   * applies). Out-of-range or unparseable values are dropped silently.
   *
   * **Retirement in progress — do not add a new producer (WEB-657 / WEB-729).**
   * DPL-07 retires the radius as a `/discover` control: once WEB-657 lands,
   * `buildDiscoverQueryArgs` stops reading this key and it becomes
   * parsed-but-ignored. Exactly one producer remains — the rail's
   * `DiscoverRadiusFilter`, which WEB-657 deletes. (WEB-729 already removed the
   * other one: Home's "Up Next" SEE ALL deep link no longer writes it.)
   * **WEB-657 therefore owns
   * deleting this key**, its parser/serializer branches and its tests, in the
   * same change that removes the slider — leaving it parsed-but-ignored is a
   * trap for the next reader. Parsing stays lenient until then so a stale
   * bookmarked `?radius_km=` is ignored silently, never a 400.
   */
  radius_km?: number;
  /**
   * Map "Search this area" viewport (FRE-1103). Serialized as
   * `"lat,lng,radiusKm"` — a center + radius triple, mirroring the
   * center+radius model the discover backend actually queries (it has no
   * bounding-box concept). Written ONLY by an explicit redo click on the map
   * takeover, never automatically on pan and never on cold load, so the
   * cold-load single-fetch invariant is preserved: when this key is absent,
   * {@link buildDiscoverQueryArgs} produces byte-identical args from the city
   * anchor. Present in a shared/deep-linked URL, SSR parses it here and feeds
   * the same args builder, so the seeded cache still matches the client key
   * (one fetch). Out-of-range or unparseable values are dropped silently so a
   * stray URL value never produces a 400.
   *
   * @since FRE-1103 (Discover · Map — "Search this area" viewport refetch)
   */
  viewport?: DiscoverViewport;
  /**
   * Followed-venue ids (FRE-1145) — opaque, no format/regex validation.
   * Multi-select, comma-joined in the URL; deduped + sorted on serialize.
   *
   * **No UI producer since WEB-662 (DPL-12).** The "Venues you follow" checkbox
   * rail that wrote this key is retired in favour of the single
   * {@link DiscoverParams.followedOnly} toggle. The key is deliberately KEPT —
   * parsed, serialized, and still forwarded to the backend by
   * `buildDiscoverQueryArgs` — rather than deleted like `radius_km`, because
   * unlike `radius_km` it is still honoured end-to-end: `discoverQuerySchema`
   * accepts it and `buildDiscoverWhere` implements `locationId: { in: venues }`.
   * A `?venue=…` deep link — including one captured inside an already-saved
   * search (WEB-663) — therefore keeps narrowing results exactly as before.
   * Deleting the key would silently widen those saved views instead of
   * preserving them.
   */
  venue: string[];
  /**
   * "Only who I follow" (WEB-662 / DPL-12) — a single boolean that replaces the
   * two followed-artists / followed-venues rails. Backed by API-1664: the
   * server resolves the viewer's follow graph from the session cookie and
   * narrows both the events list and the facet counts to events whose venue is
   * followed OR whose lineup includes a followed artist.
   *
   * Two-state in the URL: `true` serializes to `followedOnly=true`; both
   * `false` and `undefined` omit the key entirely. There is deliberately no
   * `followedOnly=false` URL form — "off" is absence, which keeps an unfiltered
   * cold load byte-identical to the pre-WEB-662 cache key (single-fetch
   * invariant) and keeps shareable links clean. Any other value (`1`, `yes`,
   * garbage) parses to `undefined` rather than throwing, so a stale or crafted
   * URL degrades to "off" instead of a 400.
   */
  followedOnly?: boolean;
  q?: string;
  view: ViewMode;
  sort: SortMode;
}

/**
 * Parsed `viewport` triple — the map's current center and an inferred search
 * radius (km). Drives {@link buildDiscoverQueryArgs} to override the city
 * anchor when the user commits a "Search this area" redo.
 */
export interface DiscoverViewport {
  /** Viewport center latitude `[-90, 90]`. */
  lat: number;
  /** Viewport center longitude `[-180, 180]`. */
  lng: number;
  /** Inferred search radius in km, clamped to `(0, 100]` (backend cap). */
  radiusKm: number;
}

/** Max search radius the discover backend accepts (`discoverQuerySchema`). */
export const MAX_VIEWPORT_RADIUS_KM = 100 as const;

/**
 * Identity for a tuple that covers EVERY key of {@link DiscoverParams}.
 *
 * When a key is missing, the parameter type becomes the `MissingDiscoverParamKeys`
 * marker below instead of `T`, so the call fails `pnpm type-check` with the
 * offending key spelled out in the expected type:
 *
 * ```
 * error TS2741: Property '__missingDiscoverParamKeys' is missing in type
 *   'readonly ["category", …]' but required in type
 *   'MissingDiscoverParamKeys<"mood">'.
 * ```
 *
 * The marker exists purely to carry that name into the diagnostic — the plain
 * `never` this used to resolve to type-checked identically but reported only
 * the keys already PRESENT, leaving the reader to diff the tuple against the
 * interface by hand.
 */
interface MissingDiscoverParamKeys<K extends keyof DiscoverParams> {
  __missingDiscoverParamKeys: K;
}

function assertCoversEveryDiscoverParam<T extends readonly (keyof DiscoverParams)[]>(
  keys: [Exclude<keyof DiscoverParams, T[number]>] extends [never]
    ? T
    : MissingDiscoverParamKeys<Exclude<keyof DiscoverParams, T[number]>>,
): T {
  return keys as T;
}

/**
 * Every key of {@link DiscoverParams}, in URL-serialization order.
 *
 * Exported (FRE-1310) as the runtime spelling of `keyof DiscoverParams`, which
 * has no runtime form of its own. It anchors the derived guard in
 * `components/organisms/DiscoverListView/__tests__/DiscoverListView.test.tsx`
 * that pins `paramsCacheKey` to the fields `buildDiscoverQueryArgs` reads —
 * the check that closes the `venue` / `followedOnly` /
 * `viewport` stale-page-append class for good. It must live HERE rather than
 * in that test file: `apps/web/tsconfig.json` excludes every `__tests__`
 * directory, so a type-level assertion written inside a test is unchecked
 * (Vitest transpiles via esbuild, which strips types without checking them).
 * In this file it is real: adding a field to `DiscoverParams` without listing
 * it here fails `pnpm --filter web type-check`.
 */
export const DISCOVER_PARAM_KEYS = assertCoversEveryDiscoverParam([
  'category',
  'date',
  'followedOnly',
  'neighborhood',
  'price',
  'priceUnlisted',
  'q',
  'radius_km',
  'sort',
  'venue',
  'view',
  'viewport',
] as const);

const MULTI_SELECT_KEYS = ['category', 'neighborhood', 'venue'] as const;
type MultiSelectKey = (typeof MULTI_SELECT_KEYS)[number];

/**
 * Returns the canonical "empty" params (no filters applied, list view, default sort).
 */
export function defaultDiscoverParams(): DiscoverParams {
  return {
    category: [],
    neighborhood: [],
    venue: [],
    view: DEFAULT_VIEW,
    sort: DEFAULT_SORT,
  };
}

function debug(message: string, value: unknown): void {
  // Soft logging so SSR and tests stay quiet by default.
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug(`[discover/url-state] ${message}`, value);
  }
}

function dedupeAndSort(values: readonly string[]): string[] {
  const cleaned = values.map((value) => value.trim()).filter((value) => value.length > 0);
  return Array.from(new Set(cleaned)).sort();
}

function parseMultiSelect(searchParams: URLSearchParams, key: MultiSelectKey): string[] {
  const all = searchParams.getAll(key).flatMap((entry) => entry.split(','));
  return dedupeAndSort(all);
}

/**
 * WEB-1221: `date` is the default ("Soonest"). A bookmarked `?sort=recommended`
 * is the old default for that same date order, so it parses as `date` with no
 * unknown-value debug log. WEB-1228 (R2) treats a retired `?sort=distance`
 * the same way. The next {@link serializeDiscoverParams} write omits
 * `sort` because the value equals {@link DEFAULT_SORT}.
 */
function parseSort(raw: string | null): SortMode {
  if (raw === null || raw === 'recommended' || raw === 'distance') return DEFAULT_SORT;
  return parseEnum(SortSchema, raw, DEFAULT_SORT, 'sort');
}

function parseEnum<T extends string>(
  schema: { safeParse: (value: unknown) => { success: true; data: T } | { success: false } },
  raw: string | null,
  fallback: T,
  fieldName: string,
): T {
  if (raw === null) return fallback;
  const result = schema.safeParse(raw);
  if (result.success) return result.data;
  debug(`unknown ${fieldName} value, falling back to "${fallback}"`, raw);
  return fallback;
}

function parseDate(raw: string | null): DateValue | undefined {
  if (raw === null || raw.length === 0) return undefined;
  const result = DateValueSchema.safeParse(raw);
  if (result.success) return result.data;
  // WEB-1282: a deep link may carry the API's wire keyword (`today`,
  // `this-weekend`) instead of the URL preset (`tonight`, `weekend`). Resolve
  // it to its preset so the chip and the filter agree with the address bar.
  // Retired keywords (`next-week`) are not wire keywords and stay dropped.
  const preset = DATE_PRESETS.find((p) => dateWindowPreset(p).keyword === raw);
  if (preset) return preset;
  debug('invalid date value, dropping', raw);
  return undefined;
}

/**
 * Parse `radius_km` from the URL. Accepts positive numbers in `[1, 100]` (the
 * backend cap). Empty / non-numeric / out-of-range values are dropped so a
 * stray URL value never produces a 400 from the discover endpoint.
 */
function parseRadiusKm(raw: string | null): number | undefined {
  if (raw === null || raw.length === 0) return undefined;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 100) {
    debug('invalid radius_km value, dropping', raw);
    return undefined;
  }
  // Round to nearest integer so we never emit fractional km on round-trip.
  return Math.round(parsed);
}

/**
 * Parse the `followedOnly` URL param (WEB-662 / DPL-12).
 *
 * Only the exact literal `"true"` turns the filter on. Everything else —
 * absent, empty, `"false"`, `"1"`, `"yes"`, or garbage — resolves to
 * `undefined` ("off"), so a stale bookmark or a crafted value silently degrades
 * to the unfiltered view rather than erroring. Returning `undefined` rather
 * than `false` for the off state is what keeps the serializer from ever
 * emitting `followedOnly=false` and perturbing the cold-load cache key.
 */
function parseFollowedOnly(raw: string | null): boolean | undefined {
  if (raw === null) return undefined;
  if (raw === 'true') return true;
  if (raw !== 'false') debug('invalid followedOnly value, treating as off', raw);
  return undefined;
}

/**
 * Parse the `priceUnlisted` URL param (WEB-1230). Same rule as
 * {@link parseFollowedOnly}: only the literal `"true"` is on.
 */
function parsePriceUnlisted(raw: string | null): boolean | undefined {
  if (raw === null) return undefined;
  if (raw === 'true') return true;
  if (raw !== 'false') debug('invalid priceUnlisted value, treating as off', raw);
  return undefined;
}

/** Precision (decimal places) used when serializing viewport coordinates. */
const VIEWPORT_COORD_PRECISION = 6;

/**
 * Parse the `viewport` URL param (`"lat,lng,radiusKm"`). Validates each
 * component against the discover backend's accepted ranges (lat `[-90, 90]`,
 * lng `[-180, 180]`, radius `(0, {@link MAX_VIEWPORT_RADIUS_KM}]`). Any
 * malformed / out-of-range component drops the whole viewport so a stray URL
 * value never produces a 400 from the discover endpoint. The radius is clamped
 * (not rejected) to the backend cap so a wide redo still returns results.
 */
function parseViewport(raw: string | null): DiscoverViewport | undefined {
  if (raw === null || raw.length === 0) return undefined;
  const parts = raw.split(',');
  if (parts.length !== 3) {
    debug('invalid viewport value (expected lat,lng,radiusKm), dropping', raw);
    return undefined;
  }
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  const radiusKm = Number(parts[2]);
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    !Number.isFinite(radiusKm) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180 ||
    radiusKm <= 0
  ) {
    debug('invalid viewport component, dropping', raw);
    return undefined;
  }
  return { lat, lng, radiusKm: Math.min(radiusKm, MAX_VIEWPORT_RADIUS_KM) };
}

/**
 * Serialize a {@link DiscoverViewport} to its `"lat,lng,radiusKm"` URL form.
 * Coordinates are fixed to {@link VIEWPORT_COORD_PRECISION} so the cache key is
 * deterministic across round-trips; the radius is rounded to a whole km and
 * re-clamped to the backend cap.
 */
function serializeViewport(viewport: DiscoverViewport): string {
  // Floor at 1 km: a positive sub-km radius (0, 0.5) would otherwise round to
  // 0, which `parseViewport` rejects (`radiusKm <= 0`) — silently dropping the
  // viewport and making a high-zoom "Search this area" redo a no-op. Clamp to
  // [1, MAX] so the emitted radius is always a valid, re-parseable integer.
  const radiusKm = Math.max(1, Math.min(Math.round(viewport.radiusKm), MAX_VIEWPORT_RADIUS_KM));
  return [
    viewport.lat.toFixed(VIEWPORT_COORD_PRECISION),
    viewport.lng.toFixed(VIEWPORT_COORD_PRECISION),
    String(radiusKm),
  ].join(',');
}

/**
 * Parse `URLSearchParams` into a fully-populated `DiscoverParams`. Unknown enum
 * values fall back to defaults silently; invalid `date` values are dropped.
 */
export function parseDiscoverParams(searchParams: URLSearchParams): DiscoverParams {
  const params = defaultDiscoverParams();

  for (const key of MULTI_SELECT_KEYS) {
    params[key] = parseMultiSelect(searchParams, key);
  }

  // Prefer `date=…`. WEB-564: accept legacy `when=…` from older `/tonight`
  // permanent redirects that cached `?when=tonight` before the destination
  // was corrected to `?date=tonight`.
  params.date = parseDate(searchParams.get('date') ?? searchParams.get('when'));

  const q = searchParams.get('q');
  if (q !== null && q.length > 0) params.q = q;

  // WEB-656 / DPL-06: normalize to a canonical preset (or clear) so an old
  // bookmarked/shared URL carrying the retired slider's `min-max` / `min+`
  // grammar never throws and never leaks a shape the picker can't represent.
  const price = normalizePricePresetParam(searchParams.get('price'));
  if (price !== undefined) params.price = price;

  // WEB-1230 — only meaningful alongside a preset; dropped without one so a
  // stray `?priceUnlisted=true` never reaches the cache key.
  const priceUnlisted = parsePriceUnlisted(searchParams.get('priceUnlisted'));
  if (priceUnlisted !== undefined && price !== undefined) params.priceUnlisted = priceUnlisted;

  const radiusKm = parseRadiusKm(searchParams.get('radius_km'));
  if (radiusKm !== undefined) params.radius_km = radiusKm;

  const viewport = parseViewport(searchParams.get('viewport'));
  if (viewport !== undefined) params.viewport = viewport;

  // WEB-662: assign only when true so `defaultDiscoverParams()` output and a
  // params object parsed from a URL without the key stay structurally
  // identical (no `followedOnly: undefined` key materialized).
  const followedOnly = parseFollowedOnly(searchParams.get('followedOnly'));
  if (followedOnly !== undefined) params.followedOnly = followedOnly;

  params.view = parseEnum(ViewSchema, searchParams.get('view'), DEFAULT_VIEW, 'view');
  params.sort = parseSort(searchParams.get('sort'));

  return params;
}

/**
 * Serialize `DiscoverParams` to `URLSearchParams` with a stable, alphabetical
 * key order. Default values (e.g. `view === 'list'`) are omitted so shareable
 * URLs stay clean and SSR cache keys remain deterministic.
 */
export function serializeDiscoverParams(params: DiscoverParams): URLSearchParams {
  const out = new URLSearchParams();

  for (const key of DISCOVER_PARAM_KEYS) {
    switch (key) {
      case 'category':
      case 'neighborhood':
      case 'venue': {
        const cleaned = dedupeAndSort(params[key] ?? []);
        if (cleaned.length > 0) out.set(key, cleaned.join(','));
        break;
      }
      case 'date': {
        const validDate = parseDate(params.date ?? null);
        if (validDate) out.set('date', validDate);
        break;
      }
      case 'followedOnly': {
        // WEB-662: emit ONLY the truthy case — "off" is the absence of the key.
        // Writing `followedOnly=false` would change the serialized URL (and via
        // `buildDiscoverQueryArgs` the RTK Query cache key) for every viewer who
        // has ever toggled the switch off, breaking the cold-load single-fetch
        // invariant for exactly the state that is supposed to be the default.
        if (params.followedOnly === true) out.set('followedOnly', 'true');
        break;
      }
      case 'q': {
        if (params.q && params.q.length > 0) out.set('q', params.q);
        break;
      }
      case 'price': {
        if (params.price && params.price.length > 0) out.set('price', params.price);
        break;
      }
      case 'priceUnlisted': {
        // WEB-1230: only with a preset, and only the truthy case.
        if (params.priceUnlisted === true && params.price) out.set('priceUnlisted', 'true');
        break;
      }
      case 'radius_km': {
        // Drop unparseable / out-of-range values during serialization so the
        // URL never carries something the backend would reject. Omit when the
        // value equals the CLT default — the backend applies that already.
        const value = parseRadiusKm(
          params.radius_km === undefined ? null : String(params.radius_km),
        );
        if (value !== undefined && value !== DEFAULT_RADIUS_KM) {
          out.set('radius_km', String(value));
        }
        break;
      }
      case 'sort': {
        if (params.sort !== DEFAULT_SORT) out.set('sort', params.sort);
        break;
      }
      case 'view': {
        if (params.view !== DEFAULT_VIEW) out.set('view', params.view);
        break;
      }
      case 'viewport': {
        // Re-validate before emitting so the URL never carries a viewport the
        // backend would reject. Omitted entirely when absent — that's the
        // cold-load default where `buildDiscoverQueryArgs` falls back to the
        // city anchor (single-fetch invariant).
        if (params.viewport) {
          const valid = parseViewport(serializeViewport(params.viewport));
          if (valid) out.set('viewport', serializeViewport(valid));
        }
        break;
      }
    }
  }

  return out;
}

/**
 * Structural equality for two `DiscoverParams` snapshots (WEB-298).
 *
 * Compares the canonical serialized form so key ordering and multi-select
 * array order are normalized away — two snapshots that write the same URL are
 * equal. Used to tell a real params transition from a re-render that merely
 * produced a new object (WEB-1096).
 */
export function discoverParamsEqual(a: DiscoverParams, b: DiscoverParams): boolean {
  return serializeDiscoverParams(a).toString() === serializeDiscoverParams(b).toString();
}
